import type Plivo from 'plivo-browser-sdk'
import { usePhoneStore } from 'v2/state/phone'
import { CallDirection } from 'v2/types/call'
import { PrimaryEvents, primaryEventBus } from '../../event-bus/primary'
import { phoneLogger } from '../../logger'
import { errorToast } from '../../toasts'
import type { ICallManager } from '../types'
import { callerAomUuid, formatPhoneNumberWithExtension } from '../utils/helpers'

export class CallManager implements ICallManager {
  sdk: Plivo | null = null

  async makeCall({
    callWith,
    contactName,
    callerId,
    parentConversationNumber,
    crmId,
    flowType,
    aomUUID,
  }: {
    callWith: string
    contactName?: string
    callerId: { name: string; number?: string }
    parentConversationNumber?: string
    crmId?: string
    flowType?: string
    aomUUID?: string
  }): Promise<void> {
    if (!this.sdk?.client || !this.sdk.client.isConnected()) {
      phoneLogger.error('Cannot make call - client not initialized or not logged in')
      let errorObj = {
        title: 'Unable to make call',
        description: 'Phone service not connected. Please reload the app and try again.',
      }

      // Check for unsupported browsers
      const isOpera = navigator.userAgent.includes('OPR')
      if (isOpera)
        errorObj = {
          title: 'Phone service not connected',
          description: 'Opera is not supported. Please use a different browser.',
        }

      errorToast(errorObj)
      return
    }

    const { formattedNumber, extension } = formatPhoneNumberWithExtension(callWith)

    if (!formattedNumber) {
      phoneLogger.error('Invalid phone number format')
      throw new Error('Invalid phone number format')
    }

    const extraHeaders: Record<string, string> = {
      'X-PH-header1': callerAomUuid(),
      'X-PH-header2': callerId.number || '',
      // Commenting this out until migration is complete
      // Once everyone is on CDP, we can add it back and send the crmId to BE
      // 'X-PH-header3': crmId || '',
      'X-PH-header4': 'web',
      'X-PH-header6': extension || '',
      'X-PH-header7': flowType || '',
      'X-PH-header8': aomUUID || '',
    }

    if (parentConversationNumber) extraHeaders['X-PH-header5'] = parentConversationNumber

    phoneLogger.info(`Initiating call to ${formattedNumber}`, { extraHeaders }, { crmId })
    const success = await this.sdk.client.call(formattedNumber, extraHeaders)

    if (success) {
      usePhoneStore.getState().setActiveCall({
        callWith: formattedNumber,
        direction: CallDirection.OUTBOUND,
        startTime: new Date(),
        contactName,
        extraHeaders: {
          meta: 'Outgoing call',
          aliasName: callerId.name || callerId.number,
        },
      })
      phoneLogger.info(`Call initiated successfully with UUID: ${this.sdk.client.callUUID}`)
    } else {
      phoneLogger.error('Failed to initiate call')
      errorToast({
        title: 'Unable to make call',
        description: 'Failed to initiate call. Please try again.',
      })
      throw new Error('Failed to initiate call')
    }
  }

  async answerCall(): Promise<void> {
    const { activeCall } = usePhoneStore.getState()
    if (!this.sdk?.client || !activeCall) {
      phoneLogger.warn('Cannot answer call - no active call or SDK not initialized')
      return
    }

    if (this.sdk.client.getCurrentSession()) {
      phoneLogger.info(`Answering call with UUID: ${activeCall.callUUID}`)
      await this.sdk.client.answer(activeCall.callUUID, '')
    } else {
      const contactUri = this.sdk.client.getContactUri()
      phoneLogger.info(`Redirecting to ${contactUri}`)
      if (contactUri) primaryEventBus.broadcast(PrimaryEvents.REDIRECT_INCOMING_CALL, contactUri)
    }
  }

  async hangup(): Promise<void> {
    const { activeCall } = usePhoneStore.getState()
    if (!this.sdk?.client || !activeCall) {
      phoneLogger.warn('Cannot hangup - no active call or SDK not initialized')
      return
    }

    phoneLogger.info(`Hanging up active call with UUID: ${activeCall.callUUID}`)
    await this.sdk.client.hangup()
  }

  async rejectCall(): Promise<void> {
    const { activeCall, isCallActive } = usePhoneStore.getState()
    if (!this.sdk?.client || !activeCall) {
      phoneLogger.warn('Cannot reject call - no active call or SDK not initialized')
      return
    }

    if (isCallActive) {
      phoneLogger.warn('Cannot reject an active call - use hangup instead')
      return
    }

    phoneLogger.info(`Rejecting incoming call with UUID: ${activeCall.callUUID}`)
    if (this.sdk.client.getCurrentSession()) {
      await this.sdk.client.reject(activeCall.callUUID)
    } else {
      primaryEventBus.broadcast(PrimaryEvents.REJECT_INCOMING_CALL, activeCall.callUUID)
    }
  }

  async redirect(toUri: string): Promise<void> {
    if (!this.sdk?.client) {
      phoneLogger.warn('Cannot redirect - SDK not initialized')
      return
    }

    phoneLogger.info(`Redirecting to ${toUri}`)
    await this.sdk.client.redirect(toUri)
  }

  isCallActive(): boolean {
    return !!this.sdk?.client?.getCurrentSession() || false
  }
}
